
import Vue from 'vue';
import { Icon, Button, Dropdown } from '@descomplica/vue-components';
import { CourseModel } from '@descomplica/dex-models';
import ContentSkeleton from '@/components/skeletons/documents/postgrad/ContentSkeleton.vue';
import { getCourseCategory } from '../helpers/category';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { TrackSkeletonLoadingInPostgradDocuments } from '@/helpers/analytics/types';

export default Vue.extend({
  components: {
    Icon,
    ContentSkeleton,
    Button,
    Dropdown,
  },
  data() {
    return {
      loading: true,
      firstLoading: true,
      contentFound: false,
      currentCourseFiles: [],
      currentCourseChosen: {},
      showDropdown: false,
    };
  },
  computed: {
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourseId(): string | undefined {
      const courseId = this.$store.getters.getCurrentChimeraCourse?._id;
      return courseId;
    },
    postgradCourses(): CourseModel[] {
      return this.$store.getters.postgradCourses;
    },
    isCurrentCoursePostgraduate(): boolean {
      return this.$store.getters.isPostgraduateCourse;
    },
  },
  async created() {
    this.currentCourseChosen = this.currentCourse;
    if (!this.isCurrentCoursePostgraduate) {
      this.$router.push({
        name: 'home',
        params: {
          category: this.currentCourse.descomplicaCategorySlug as string,
          currentCourse: this.currentCourse.slug as string,
        },
      });
    }
    if (this.currentChimeraCourseId) {
      await this.pullCourseFiles(this.currentChimeraCourseId, this.currentCourse.name);
    }
    this.firstLoading = false;
    this.loading = false;
  },
  methods: {
    verifyIfNotLastElement(index: number): boolean {
      return index !== this.currentCourseFiles.length - 1;
    },

    downloadFile(fileURL: string, fileName: string, courseName: string): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        in_postgrad_document_download: true,
        file: fileName,
        from_course: courseName,
        changed_course: this.hasChangedCourse(courseName),
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
      });
      window.open(fileURL, '_blank');
    },

    async pullCourseFiles(chimeraCourseId: string | null, courseName: string): Promise<void> {
      this.loading = true;
      const startTime = Date.now();
      if (chimeraCourseId) {
        await this.$store.dispatch('GET_CHIMERA_COURSE_MATERIAL_FILES', {
          chimeraCourseId,
          currentCourse: this.currentCourse,
          courseName,
        });
        this.getCourseFiles(chimeraCourseId);
      }
      this.loading = false;
      const endTime = Date.now();
      const timePassed = (endTime - startTime) / 1000;

      this.trackTimeOfSkeleton(courseName, timePassed);
    },

    getCourseFiles(chimeraCourseId: string | null): boolean {
      if (!chimeraCourseId) return false;

      const files = this.$store.getters.chimeraCourseMaterials(chimeraCourseId);
      this.contentFound = !!files && files.length > 0;
      this.currentCourseFiles = files;

      return this.contentFound;
    },

    arrangedItemsForDropdown(): string[] {
      return this.postgradCourses.map((course: CourseModel) => {
        return course.name;
      });
    },

    async changeCurrentCourseChosen(courseName: string, event: Event): Promise<void> {
      const index = this.arrangedItemsForDropdown().indexOf(courseName);
      const course = this.postgradCourses[index];
      Analytics.trackEvent(EVENT_LABELS.click, {
        in_dropdown_postgrad_documents_change_course: true,
        changed_course: this.hasChangedCourse(course.name),
        to_course: course.name,
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
      });

      this.currentCourseChosen = course;
      const foundFiles = this.getCourseFiles(course.descomplicaCourseId);

      this.closeDropdown();
      if (!foundFiles) {
        await this.pullCourseFiles(course.descomplicaCourseId, course.name);
      }
      event.stopPropagation();
    },

    toggleDropdown(event: Event): void {
      this.showDropdown = !this.showDropdown;
      event.stopPropagation();
    },

    closeDropdown(): void {
      this.showDropdown = false;
    },

    async tryGettingDocumentsAgain(
      chimeraCourseId: string | null,
      courseName: string,
    ): Promise<void> {
      this.loading = true;
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        in_postgrad_try_again_documents: true,
        in_course: courseName,
        changed_course: this.hasChangedCourse(courseName),
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
      });

      if (!chimeraCourseId) {
        this.loading = false;
        return;
      }

      await this.pullCourseFiles(chimeraCourseId, courseName);
    },
    hasChangedCourse(courseName: string): boolean {
      return this.currentCourse.name !== courseName;
    },
    trackTimeOfSkeleton(courseName: string, timePassed: number): void {
      Analytics.trackEvent(EVENT_LABELS.skeletonLoadingTime, {
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
        changed_course: this.hasChangedCourse(courseName),
        in_course: courseName,
        in_postgrad_document_page: true,
        time_passed: timePassed,
      } as TrackSkeletonLoadingInPostgradDocuments);
    },
  },
});
